import { ModButton } from '@moderneinc/design-system-components';
import {
  semanticColors,
  semanticTypography,
  spacing
} from '@moderneinc/design-system-tokens';
import { Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';
import { ACCOUNT_SETTINGS_PATH } from '../../constants/general';
import { ShortcutKey } from '../keyboard-shortcuts/keyboard-shortcuts.component';
import { OrganizationsApiExample } from './organizations-api-example.component';

const KeyHint: FunctionComponent<{
  keys: string[];
  label: string;
}> = ({ keys, label }) => (
  <Stack
    direction="row"
    sx={{
      gap: 0.5,
      alignItems: 'center'
    }}
  >
    {keys.map((key) => (
      <ShortcutKey key={key}>{key}</ShortcutKey>
    ))}
    <Typography
      sx={{
        ...semanticTypography.body.small.regular,
        color: 'text.secondary'
      }}
    >
      {label}
    </Typography>
  </Stack>
);

const KeyboardShortcuts: FunctionComponent = () => (
  <Stack
    direction="row"
    sx={{
      gap: `${spacing.spacing_2}px`,
      alignItems: 'center',
      display: { xs: 'none', md: 'flex' }
    }}
  >
    <KeyHint keys={['↑', '↓']} label="Navigate" />
    <KeyHint keys={['Enter']} label="Select" />
    <KeyHint keys={['Esc']} label="Close" />
  </Stack>
);

const USER_ORGANIZATIONS_TAB_PATH = `${ACCOUNT_SETTINGS_PATH}/user-organizations`;

type OrganizationsFooterProps = {
  onClose?: () => void;
};

export const OrganizationsFooter: FunctionComponent<
  OrganizationsFooterProps
> = ({ onClose }) => {
  const router = useRouter();

  const handleManageClick = () => {
    onClose?.();
    router.push(USER_ORGANIZATIONS_TAB_PATH);
  };

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 1,
        px: 2.5,
        py: 1.5,
        borderTop: `1px solid ${semanticColors.color.border.primary}`,
        flexShrink: 0
      }}
    >
      <KeyboardShortcuts />
      <Stack
        direction="row"
        sx={{ gap: `${spacing.spacing_2}px`, alignItems: 'center', ml: 'auto' }}
      >
        <OrganizationsApiExample />
        <ModButton
          size="small"
          variant="tertiary"
          onClick={handleManageClick}
          data-testid="manage-user-organizations-button"
        >
          Manage user organizations
        </ModButton>
      </Stack>
    </Stack>
  );
};
